// mail-policy.ts — which mails may be sent without human review. Pure module, no dependencies.
//
// This business rule lives here rather than in the edge function so it can be unit tested; the
// edge imports it, so there is a single source of truth.
//
// Two locks, because a mail is defined by two things: what it says (`AUTHORIZED_SUBJECT_PREFIXES`,
// only listed subjects may go out alone) and who it talks to (`internalDomains`, a recipient
// outside UPSIZE is not a colleague). The second closes the case of an allowed subject
// mis-targeted at a client.
//
// When in doubt nothing is sent and nothing is discarded: the mail stays `pending`
// and visible so a human decides. A late mail can be recovered; a sent one cannot.

/**
 * Subject prefixes allowed to be sent automatically. This is a whitelist matched with
 * `startsWith`, so the French subjects must stay exactly as the sending side emits them.
 * Widening this list is a decision, not a patch.
 */
export const AUTHORIZED_SUBJECT_PREFIXES = [
  "[Quote Tool] Matière(s) provisoire(s)",
  "[Quote Tool] Dérive de prix en production",
];

/** Domains treated as internal by default. Overridden by `MAIL_DOMAINES_INTERNES` in the edge function. */
export const DEFAULT_INTERNAL_DOMAINS = ["upsize.fr"];

/** A mail waiting to be routed. */
export interface MailToRoute {
  subject: string;
  recipients: string[] | null;
}

/** Context of the sending decision. */
export interface PolicyContext {
  /** Is a sending key configured? Without it nothing can be sent, and that is reported. */
  configuredKey: boolean;
  /** Internal domains in force. Empty disables the domain lock (deliberate, never the default). */
  internalDomains: string[];
}

/** Outcome of the sending decision. */
export type Decision =
  /** Cleared to send. */
  | { action: "send" }
  /** Leave it alone: the row stays `pending` and visible, awaiting a human decision. */
  | { action: "leave"; reason: string }
  /** Terminal: the row carries both its status and its reason, so it can be replayed knowingly. */
  | { action: "mark"; status: "failed" | "not_configured"; reason: string };

/**
 * Domain of an address, lowercased. Empty when the address has none, so never internal.
 * @param address The email address.
 * @returns The lowercased domain, or an empty string.
 */
function domainOf(address: string): string {
  const i = address.lastIndexOf("@");
  return i < 0 ? "" : address.slice(i + 1).trim().toLowerCase();
}

/**
 * Sending decision for one mail. The check order is intentional: both scope locks run before any
 * technical diagnosis, so a mail that should not go out is never marked `failed` or `not_configured`
 * (terminal statuses that would report a failure where there is only a deliberate refusal).
 * @param mail The mail to route.
 * @param ctx The policy context.
 * @returns The decision; reasons are French (stored on the mail row and shown to humans).
 */
export function decideDispatch(mail: MailToRoute, ctx: PolicyContext): Decision {
  // 1. What the mail says.
  if (!AUTHORIZED_SUBJECT_PREFIXES.some((p) => mail.subject.startsWith(p))) {
    return { action: "leave", reason: "type de mail hors envoi automatique — relecture humaine requise" };
  }

  // 2. Who it talks to. An internal mail aimed outside is no longer internal.
  const recipients = mail.recipients ?? [];
  if (ctx.internalDomains.length) {
    const internal = ctx.internalDomains.map((d) => d.trim().toLowerCase()).filter(Boolean);
    const external = recipients.filter((a) => !internal.includes(domainOf(a)));
    if (external.length) {
      return {
        action: "leave",
        reason: `destinataire(s) hors UPSIZE (${external.join(", ")}) — relecture humaine requise`,
      };
    }
  }

  // 3. Role targeting found nobody. Not a sending failure, but not replayable as-is either, so stop and say so.
  if (!recipients.length) {
    return {
      action: "mark",
      status: "failed",
      reason: "aucun destinataire — aucun superviseur/admin actif au moment de la validation",
    };
  }

  // 4. Nothing objects to sending, but nothing allows it either.
  if (!ctx.configuredKey) {
    return { action: "mark", status: "not_configured", reason: "RESEND_API_KEY absente de l'environnement" };
  }

  return { action: "send" };
}
